// ThemeContext.tsx — Contexto global de tema claro/oscuro
// El tema inicial siempre sigue la configuración del dispositivo/navegador
// del visitante (prefers-color-scheme) — no se persiste en localStorage.
// Así, cada vez que alguien entra (o vuelve a entrar) al sitio, ve el
// tema que tiene configurado en su sistema, sin importar si en una
// visita anterior tocó el botón de cambio de tema. Aplica el atributo
// data-theme al <html> para que las variables CSS de styles.css (:root
// vs :root[data-theme="light"]) cambien de valor en toda la app.
import { createContext, useContext, useState, useCallback, useLayoutEffect } from 'react';
import type { ReactNode } from 'react';

export type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const getInitialTheme = (): Theme => {
  try {
    if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  } catch { /* ignore */ }
  return 'dark';
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  // useLayoutEffect (no useEffect) para aplicar el atributo antes del
  // primer paint tras un cambio, evitando cualquier parpadeo del tema.
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Cambia el tema solo para la sesión/pestaña actual — no se guarda,
  // así que al recargar o volver a entrar se vuelve a leer el dispositivo.
  const toggleTheme = useCallback((): void => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe estar dentro de <ThemeProvider>');
  return ctx;
}
